// src/pages/qc/EquipmentDetailModal.jsx
import { useState, useEffect } from "react";
import * as api from "../../apiService";
import { BASE_URL } from "../../apiService";
import { fmtDate, todayDubai } from "../../utils/dateUtils";
import { EquipmentCreateModal } from "./EquipmentCreateModal";

const CAL_STATUS = {
  OK:               { bg: "#d1fae5", color: "#065f46", label: "OK" },
  DUE_SOON:         { bg: "#fef3c7", color: "#92400e", label: "Due Soon" },
  OVERDUE:          { bg: "#fee2e2", color: "#991b1b", label: "Overdue" },
  NEVER_CALIBRATED: { bg: "#e0e7ff", color: "#3730a3", label: "Never Calibrated" },
  NOT_REQUIRED:     { bg: "#f3f4f6", color: "#6b7280", label: "Not Required" },
};

function CalStatusBadge({ status }) {
  const s = CAL_STATUS[status] ?? CAL_STATUS.NOT_REQUIRED;
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 4,
      padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
      {s.label}
    </span>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
      <span style={{ color: "#6b7280", fontSize: 12, minWidth: 160 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 500, color: value ? "#111" : "#bbb" }}>
        {value || "—"}
      </span>
    </div>
  );
}

function AddCalibrationForm({ equipmentId, onSaved, onCancel }) {
  const [form, setForm] = useState({
    calibrationDate: todayDubai(),
    calibrationType: "",
    certificateNo:   "",
    nextDueDate:     "",
    result:          "PASS",
    performedBy:     "",
    notes:           "",
  });
  const [certFile, setCertFile] = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState(null);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = {
        ...form,
        nextDueDate:     form.nextDueDate     || null,
        calibrationType: form.calibrationType || null,
      };
      let record = await api.addCalibration(equipmentId, body);
      if (certFile) {
        record = await api.uploadCalibrationCertificate(record.id, certFile);
      }
      onSaved(record);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ background: "#f8fafc", borderRadius: 8,
      border: "1px solid #e2e8f0", padding: 16, marginTop: 12 }}>
      <div style={{ fontWeight: 600, fontSize: 12, color: "#1a3a6b",
        borderBottom: "2px solid #1a3a6b", marginBottom: 12, paddingBottom: 4 }}>
        Add Calibration Record
      </div>

      <div className="form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Calibration Date *</label>
          <input className="form-input" type="date" required value={form.calibrationDate}
            onChange={e => set("calibrationDate", e.target.value)} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Next Due Date</label>
          <input className="form-input" type="date" value={form.nextDueDate}
            onChange={e => set("nextDueDate", e.target.value)} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Result</label>
          <select className="form-select" value={form.result}
            onChange={e => set("result", e.target.value)}>
            <option value="PASS">Pass</option>
            <option value="FAIL">Fail</option>
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Calibration Type</label>
          <select className="form-select" value={form.calibrationType}
            onChange={e => set("calibrationType", e.target.value)}>
            <option value="">— Select —</option>
            <option value="INTERNAL">Internal</option>
            <option value="EXTERNAL">External</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Certificate No</label>
          <input className="form-input" value={form.certificateNo}
            onChange={e => set("certificateNo", e.target.value)} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Performed By</label>
          <input className="form-input" value={form.performedBy}
            onChange={e => set("performedBy", e.target.value)} />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group" style={{ flex: 2 }}>
          <label className="form-label">Notes</label>
          <input className="form-input" value={form.notes}
            onChange={e => set("notes", e.target.value)} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label className="form-label">Certificate File</label>
          <input className="form-input" type="file" accept=".pdf,image/*"
            onChange={e => setCertFile(e.target.files?.[0] || null)} />
          {certFile && (
            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 3 }}>
              {certFile.name}
            </div>
          )}
        </div>
      </div>

      {error && <div className="alert-warn">{error}</div>}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? (certFile ? "Saving & uploading…" : "Saving…") : "Save Record"}
        </button>
      </div>
    </form>
  );
}

export function EquipmentDetailModal({ equipment, canEdit, canDelete, onUpdate, onDelete, onEquipmentUpdated, onClose }) {
  const [eq,            setEq]            = useState(equipment);
  const [calibrations,  setCalibrations]  = useState([]);
  const [loadingCal,    setLoadingCal]    = useState(true);
  const [showAddCal,    setShowAddCal]    = useState(false);
  const [showEdit,      setShowEdit]      = useState(false);
  const [deletingId,    setDeletingId]    = useState(null);
  const [uploadingId,   setUploadingId]   = useState(null);
  const [certActionId,  setCertActionId]  = useState(null);

  useEffect(() => {
    api.fetchEquipmentCalibrations(eq.id)
      .then(setCalibrations)
      .finally(() => setLoadingCal(false));
  }, [eq.id]);

  async function handleUpdate(body) {
    const updated = await onUpdate(eq.id, body);
    if (updated) setEq(updated);
    setShowEdit(false);
  }

  async function handleUploadCertificate(calibrationId, file) {
    setUploadingId(calibrationId);
    try {
      const updated = await api.uploadCalibrationCertificate(calibrationId, file);
      setCalibrations(old => old.map(c => c.id === updated.id ? updated : c));
    } catch (err) {
      alert("Upload failed: " + err.message);
    } finally {
      setUploadingId(null);
    }
  }

  async function handleDeleteCertificate(calibrationId) {
    if (!window.confirm("Remove the certificate file?")) return;
    setCertActionId(calibrationId);
    try {
      const updated = await api.deleteCalibrationCertificate(calibrationId);
      setCalibrations(old => old.map(c => c.id === updated.id ? updated : c));
    } catch (err) {
      alert("Delete failed: " + err.message);
    } finally {
      setCertActionId(null);
    }
  }

  function handleViewCertificate(calibrationId) {
    const token = localStorage.getItem("id_token");
    fetch(`${BASE_URL}/api/rd/equipment/calibrations/${calibrationId}/certificate`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async res => {
        if (!res.ok) throw new Error("Failed to load certificate");
        const blob = await res.blob();
        const url  = URL.createObjectURL(blob);
        window.open(url, "_blank");
      })
      .catch(e => alert(e.message));
  }

  function refreshEquipmentStatus() {
    api.fetchEquipment().then(all => {
      const fresh = all.find(e => e.id === eq.id);
      if (fresh) {
        setEq(fresh);
        onEquipmentUpdated?.(fresh);
      }
    });
  }

  function handleCalibrationSaved(record) {
    setCalibrations(old => [record, ...old]);
    setShowAddCal(false);
    refreshEquipmentStatus();
  }

  async function handleDeleteCalibration(id) {
    if (!window.confirm("Delete this calibration record?")) return;
    setDeletingId(id);
    try {
      await api.deleteCalibration(id);
      setCalibrations(old => old.filter(c => c.id !== id));
      refreshEquipmentStatus();
    } finally {
      setDeletingId(null);
    }
  }

  if (showEdit) {
    return (
      <EquipmentCreateModal
        equipment={eq}
        onSave={handleUpdate}
        onCancel={() => setShowEdit(false)}
      />
    );
  }

  return (
    <div className="modal" style={{ width: 700, maxHeight: "90vh", overflowY: "auto" }}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{eq.name}</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              {eq.equipmentNo && <span style={{ fontFamily: "monospace" }}>{eq.equipmentNo} · </span>}
              <CalStatusBadge status={eq.calibrationStatus} />
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">

          <div style={{ fontWeight: 600, fontSize: 12, color: "#1a3a6b",
            borderBottom: "2px solid #1a3a6b", marginBottom: 12, paddingBottom: 4,
            display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Equipment Details</span>
            {canEdit && (
              <button className="btn btn-sm btn-secondary" onClick={() => setShowEdit(true)}>
                Edit
              </button>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
            <DetailRow label="Equipment No"         value={eq.equipmentNo} />
            <DetailRow label="Location"             value={eq.location} />
            <DetailRow label="Quantity"             value={eq.quantity} />
            <DetailRow label="Maker / Brand"        value={eq.maker} />
            <DetailRow label="Range"                value={eq.range} />
            <DetailRow label="Least Count"          value={eq.leastCount} />
            <DetailRow label="Calibration Required" value={eq.calibrationRequired ? "Yes" : "No"} />
            <DetailRow label="Frequency"            value={eq.calibrationFrequency} />
            <DetailRow label="Calibration Type"     value={eq.calibrationType} />
            <DetailRow label="Expiry Date"          value={eq.expiryDate ? fmtDate(eq.expiryDate) : null} />
            <DetailRow label="Last Calibrated"      value={eq.lastCalibrationDate ? fmtDate(eq.lastCalibrationDate) : null} />
            <DetailRow label="Next Due Date"        value={eq.nextDueDate ? fmtDate(eq.nextDueDate) : null} />
            <DetailRow label="Added On"             value={fmtDate(eq.createdAt)} />
          </div>

          {eq.notes && (
            <div style={{ marginTop: 8, background: "#f9fafb", borderRadius: 6,
              padding: "8px 12px", fontSize: 12, color: "#374151" }}>
              {eq.notes}
            </div>
          )}

          <div style={{ fontWeight: 600, fontSize: 12, color: "#1a3a6b",
            borderBottom: "2px solid #1a3a6b", marginBottom: 12, paddingBottom: 4,
            marginTop: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Calibration History</span>
            {canEdit && eq.calibrationRequired && !showAddCal && (
              <button className="btn btn-sm btn-primary" onClick={() => setShowAddCal(true)}>
                + Add Record
              </button>
            )}
          </div>

          {showAddCal && (
            <AddCalibrationForm
              equipmentId={eq.id}
              onSaved={handleCalibrationSaved}
              onCancel={() => setShowAddCal(false)}
            />
          )}

          {loadingCal ? (
            <div style={{ color: "#aaa", fontSize: 12 }}>Loading calibration records…</div>
          ) : calibrations.length === 0 ? (
            <div style={{ color: "#9ca3af", fontSize: 13, textAlign: "center", padding: "16px 0" }}>
              No calibration records yet
            </div>
          ) : (
            <table className="data-table" style={{ marginTop: 0 }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Certificate No</th>
                  <th>Next Due</th>
                  <th>Result</th>
                  <th>Performed By</th>
                  <th>Certificate File</th>
                  {canEdit && <th></th>}
                </tr>
              </thead>
              <tbody>
                {calibrations.map(c => (
                  <tr key={c.id}>
                    <td>{fmtDate(c.calibrationDate)}</td>
                    <td style={{ fontSize: 12 }}>{c.calibrationType || "—"}</td>
                    <td style={{ fontFamily: "monospace", fontSize: 12 }}>{c.certificateNo || "—"}</td>
                    <td style={{ fontSize: 12 }}>{c.nextDueDate ? fmtDate(c.nextDueDate) : "—"}</td>
                    <td>
                      <span style={{
                        background: c.result === "PASS" ? "#d1fae5" : "#fee2e2",
                        color:      c.result === "PASS" ? "#065f46" : "#991b1b",
                        borderRadius: 4, padding: "1px 6px", fontSize: 11, fontWeight: 600 }}>
                        {c.result}
                      </span>
                    </td>
                    <td style={{ fontSize: 12 }}>{c.performedBy || "—"}</td>
                    <td>
                      {c.certificateKey ? (
                        <div style={{ display: "flex", gap: 4 }}>
                          <button className="btn btn-sm btn-secondary"
                            onClick={() => handleViewCertificate(c.id)}>
                            View
                          </button>
                          {canEdit && (
                            <button className="btn btn-sm btn-danger"
                              disabled={certActionId === c.id}
                              onClick={() => handleDeleteCertificate(c.id)}>
                              {certActionId === c.id ? "…" : "Remove"}
                            </button>
                          )}
                        </div>
                      ) : canEdit ? (
                        <>
                          <label style={{ cursor: "pointer" }}>
                            <input type="file" accept=".pdf,image/*" style={{ display: "none" }}
                              disabled={uploadingId === c.id}
                              onChange={e => {
                                const f = e.target.files?.[0];
                                if (f) handleUploadCertificate(c.id, f);
                                e.target.value = "";
                              }} />
                            <span className="btn btn-sm btn-secondary">
                              {uploadingId === c.id ? "Uploading…" : "Upload"}
                            </span>
                          </label>
                        </>
                      ) : (
                        <span style={{ color: "#bbb", fontSize: 12 }}>—</span>
                      )}
                    </td>
                    {canEdit && (
                      <td>
                        <button className="btn btn-sm btn-danger"
                          disabled={deletingId === c.id}
                          onClick={() => handleDeleteCalibration(c.id)}>
                          {deletingId === c.id ? "…" : "Delete"}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

        </div>

        <div className="modal-footer">
          {canDelete && (
            <button className="btn btn-danger"
              onClick={() => onDelete(eq.id)}>
              Remove Equipment
            </button>
          )}
          <button className="btn btn-secondary" style={{ marginLeft: "auto" }} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
  );
}
